import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    input,
    computed,
    output,
    signal,
    effect,
    model,
} from "@angular/core";
import { BpeModule } from "../../bpe/bpe.module";
import { FormsModule } from "@angular/forms";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { Doc } from "./s25.document.const";
import { NgTemplateOutlet, SlicePipe, TitleCasePipe, UpperCasePipe } from "@angular/common";
import { ReportBuilderCellFunctionArgument } from "./s25.document.report.builder.table.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25Util } from "../../../util/s25-util";
import { DocumentUtil } from "./s25.document.util";

@Component({
    standalone: true,
    selector: "s25-ng-document-report-builder-cell-editor-function",
    template: `
        @if (spec().info) {
            <span class="function-info">{{ spec().info }}</span>
        }
        <div class="arguments">
            @for (definedArg of definedArguments(); track $index) {
                @let arg = definedArg.arg;
                @let def = definedArg.def;
                @let hasHeader = def.arg.label || def.arg.type === "multi";

                <div class="argument-wrapper" [class.invalid]="arg.error()">
                    @if (hasHeader) {
                        <div class="argument-header">
                            <div>
                                @if (def.arg.label) {
                                    <label class="argument-label">{{ def.arg.label }}</label>
                                }
                            </div>
                            <div class="argument-types">
                                @if (def.arg.type === "multi") {
                                    @for (option of def.arg.options; track option) {
                                        <s25-ng-button
                                            class="argument-type-button"
                                            [class.active]="arg.type === option.type"
                                            [type]="'none'"
                                            (click)="onArgTypeChange(arg, option)"
                                            [title]="'Switch to ' + (option.type | titlecase) + ' input'"
                                            [ariaLabel]="'Switch to ' + (option.type | titlecase) + ' input'"
                                            >{{ option.type | uppercase | slice: 0 : 1 }}</s25-ng-button
                                        >
                                    }
                                }
                            </div>
                        </div>
                    }

                    <ng-container
                        [ngTemplateOutlet]="argument"
                        [ngTemplateOutletContext]="{ i: $index, arg: arg, canDelete: def.type === 'variable' }"
                    />
                </div>
            }
        </div>
        @if (!!spec().variableArguments) {
            <div class="buttons">
                @for (arg of spec().variableArguments; track $index) {
                    <s25-ng-button class="add-argument" [type]="'outline'" (click)="addArgument(arg)">
                        {{ arg.buttonLabel }}
                    </s25-ng-button>
                }
            </div>
        }

        <ng-template #argument let-i="i" let-arg="arg" let-canDelete="canDelete">
            <div class="argument">
                <div>
                    @if (arg.type === "text") {
                        <input
                            type="text"
                            class="c-input"
                            [class.invalid]="arg.error()"
                            [(ngModel)]="arg.text"
                            (ngModelChange)="changed.emit()"
                        />
                    }
                    @if (arg.type === "variable" && !arg.variableType) {
                        <s25-ng-bpe-variable
                            [state]="arg.state"
                            [type]="documentType()"
                            [codeMode]="false"
                            [documentType]="documentType()"
                            [hasPost]="false"
                            [hasFormatExample]="false"
                            [prefix]="false"
                            [wrap]="false"
                            [hasNoneItem]="true"
                            [scrollIntoView]="false"
                            [hasFormat]="!arg.noFormat"
                            [placeholder]="' '"
                            (variableChange)="changed.emit()"
                        />
                    }
                    @if (arg.type === "variable" && arg.variableType) {
                        <s25-ng-bpe-variable
                            [state]="arg.state"
                            [type]="documentType()"
                            [dataTypes]="[arg.variableType]"
                            [codeMode]="false"
                            [documentType]="documentType()"
                            [hasPost]="false"
                            [hasFormatExample]="false"
                            [prefix]="false"
                            [wrap]="false"
                            [hasNoneItem]="true"
                            [scrollIntoView]="false"
                            [hasFormat]="!arg.noFormat"
                            [placeholder]="' '"
                            (variableChange)="changed.emit()"
                        />
                    }
                    @if (arg.type === "comparisonOperator") {
                        <select class="cn-form__control" [(ngModel)]="arg.text" (ngModelChange)="changed.emit()">
                            <option value="eq">=</option>
                            <option value="ne">≠</option>
                            <option value="lt"><</option>
                            <option value="le"><=</option>
                            <option value="gt">></option>
                            <option value="ge"><=</option>
                        </select>
                    }
                    @if (!!arg.error()) {
                        <span class="error ngRed">{{ arg.error() }}</span>
                    }
                </div>

                @if (canDelete) {
                    <button class="deleteArgument dot" (click)="deleteArgument(i)">
                        <s25-ng-icon [type]="'minus'" [label]="'Delete'" />
                    </button>
                }
            </div>
        </ng-template>
    `,
    styles: `
        :host {
            --argument-color: color-mix(in hsl, var(--cn-blue), #fff0 92%);
            --invalid-argument-color: rgba(167, 37, 37, 0.08);
        }

        ::ng-deep body.nm-party--on s25-ng-document-report-builder-cell-editor {
            --argument-color: color-mix(in hsl, var(--cn-blue), #fff0 70%);
            --invalid-argument-color: rgba(167, 37, 37, 0.08);
        }

        .buttons {
            display: flex;
            justify-content: space-evenly;
        }

        .argument-wrapper {
            padding: 0.5rem;
            border-radius: var(--border-radius);
            margin-bottom: 0.5rem;
            background: var(--argument-color);
            display: flex;
            flex-direction: column;
        }

        .argument-wrapper.invalid {
            background: var(--invalid-argument-color);
        }

        .argument-label {
            font-weight: bold;
        }

        .argument {
            display: flex;
            justify-content: space-between;
            gap: 0.5rem;
        }

        .argument > :first-child {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
        }

        .argument > :first-child input {
            width: 100%;
        }

        .deleteArgument {
            background-color: var(--red);
            border-radius: 50%;
            aspect-ratio: 1;
            font-size: 0.6em;
            align-self: center;
            position: relative;
            height: 2em;
            border: 0;
            cursor: pointer;

            s25-ng-icon {
                top: 50%;
                left: 50%;
                translate: -50% -50%;
                font-size: 0.6rem;
                color: white;
                position: absolute;
            }
        }

        .argument-header {
            display: flex;
            justify-content: space-between;
        }

        .argument-types {
            display: flex;
            gap: 0.5rem;
            padding-bottom: 0.25rem;
        }

        .argument-type-button {
            width: 1rem;
            height: 1rem;
            background: color-mix(in hsl, var(--cn-blue), transparent 50%);
            color: white;
            border-radius: 4px;
            line-height: 0;
        }

        .argument-type-button.active {
            background: var(--cn-blue);
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        BpeModule,
        FormsModule,
        S25ButtonComponent,
        S25IconComponent,
        NgTemplateOutlet,
        SlicePipe,
        UpperCasePipe,
        TitleCasePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25DocumentReportBuilderCellEditorFunctionComponent {
    arguments = model.required<ReportBuilderCellFunctionArgument[]>();
    type = input.required<Doc.ReportBuilderFunction>();
    documentType = input.required<Doc.ReportBuilderTypeId>();

    changed = output<void>();

    spec = computed(() => Doc.reportBuilderFunctions[this.type()]);
    definedArguments = computed(() =>
        this.arguments().map((arg, i) => {
            const test = {
                arg,
                def: DocumentUtil.getArgumentDefinition(this.spec(), arg.state, i, true),
            };
            return test;
        }),
    );

    constructor() {
        // Ensure that all fixed arguments are included
        effect(() => {
            let changed = false;
            const args = [...this.arguments()];
            for (let i = 0; i < this.spec().fixedArguments?.length; i++) {
                let spec = this.spec().fixedArguments[i];
                const arg = args[i];
                if (arg && spec.type === "multi" && spec.options.some((opt) => arg.type === opt.type)) {
                    continue;
                }
                if (arg?.type === spec.type) continue;
                args.splice(i, 0, this.createArgument(spec));
                changed = true;
            }
            if (changed) this.arguments.set(args);
            this.changed.emit();
        });
    }

    addArgument(arg: Doc.ReportBuilderFunctionArgument) {
        this.arguments.update((args) => [...args, this.createArgument(arg)]);
        this.changed.emit();
    }

    deleteArgument(index: number) {
        this.arguments.update((args) => [...args.slice(0, index), ...args.slice(index + 1)]);
        this.changed.emit();
    }

    createArgument(arg: Doc.ReportBuilderFunctionArgument): ReportBuilderCellFunctionArgument {
        const type = arg.type;
        switch (type) {
            case "text":
                return { ...arg, text: "", error: signal(undefined) };
            case "variable":
                return { ...arg, state: { options: {} }, error: signal(undefined) };
            case "comparisonOperator":
                return { ...arg, text: "", error: signal(undefined) };
            case "multi":
                return this.createArgument(arg.options[0]);
        }
        return undefined;
    }

    onArgTypeChange(arg: ReportBuilderCellFunctionArgument, spec: ReportBuilderCellFunctionArgument) {
        if (arg.type === spec.type) return;
        S25Util.clearObject(arg);
        Object.assign(arg, this.createArgument(spec));
        this.changed.emit();
    }
}
